// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DateTimeCalendar.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Moment from 'moment';
import { assign, isNil, isUndefined, map, isEmpty } from 'lodash/fp';
import { DatePicker, DateRangePicker } from '@blueprintjs/datetime';
import { Classes, Boundary } from '@blueprintjs/core';
import TimePicker from './Izenda.TimePicker';
import { isDateTimeValid } from 'share/utils/Izenda.DateTimeUtils';

const DatePickerType = 'DatePicker';
const TimePickerType = 'TimePicker';
const DateRangePickerType = 'DateRangePicker';

const START_DATE_INDEX = 0;
const END_DATE_INDEX = 1;

const modifiers = {
  toDay: new Date()
};

export default class DateTimeCalendar extends PureComponent {
  static propTypes = {
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]), // Moment object or array of Moment object
    onSelect: PropTypes.func.isRequired,
    hasRange: PropTypes.bool,
    hasDate: PropTypes.bool,
    hasTime: PropTypes.bool,
    minDate: PropTypes.object,
    maxDate: PropTypes.object
  };

  originalRange = [null, null];

  constructor(props) {
    super(props);
    const { hasRange, value } = this.props;
    hasRange && (this.originalRange = value || [null, null]);
  }

  componentWillUpdate() {
    const { hasRange, value } = this.props;
    hasRange && (this.originalRange = value || [null, null]);
  }

  /**
   * Resolve component and its default props
   * @param {string} type
   */
  getComponent = (type: string) => {
    switch (type) {
      case DatePickerType:
        return {
          Component: DatePicker,
          defaultProps: {
            showActionsBar: true,
            canClearSelection: false,
            dayPickerProps: {
              modifiers
            }
          }
        };
      case TimePickerType:
        return {
          Component: TimePicker
        };
      case DateRangePickerType:
        return {
          Component: DateRangePicker,
          defaultProps: {
            shortcuts: false,
            allowSingleDayRange: true
          }
        };
    }
    return {};
  };

  /**
   * Resolve type of component
   * @param {boolean} hasRange
   * @param {boolean} hasDate
   * @param {boolean} hasTime
   */
  getType = (hasRange: boolean, hasDate: boolean, hasTime: boolean) => {
    switch (true) {
      case hasRange && hasDate && hasTime:
        return DateRangePickerType;
      case hasRange && hasDate && !hasTime:
        return DateRangePickerType;
      case hasTime && !hasDate:
        return TimePickerType;
      case hasRange && !hasDate && hasTime:
        return TimePickerType;
      default:
        return DatePickerType;
    }
  };

  /**
   * On change date time picker value
   */
  onChange = (selectedDate: Date, isUserChange: boolean) => {
    const { onSelect } = this.props;
    if (isUserChange || isUndefined(isUserChange)) {
      const value = !isNil(selectedDate) ? Moment(selectedDate) : null;
      onSelect && onSelect(value);
    }
  };

  /**
   * On change range picker value
   */
  onChangeRange = (selectedDates: Array<Date>) => {
    const { onSelect, boundaryToModify, minDate, maxDate } = this.props;
    const dateTimes = map((date: Date) => (!isNil(date) ? Moment(date) : null), selectedDates);

    if (
      boundaryToModify !== Boundary.START &&
      dateTimes[START_DATE_INDEX] &&
      this.originalRange[START_DATE_INDEX] &&
      this.originalRange[START_DATE_INDEX] < minDate
    ) {
      dateTimes[START_DATE_INDEX] = this.originalRange[START_DATE_INDEX];
    }

    if (
      boundaryToModify !== Boundary.END &&
      dateTimes[END_DATE_INDEX] &&
      this.originalRange[END_DATE_INDEX] &&
      this.originalRange[END_DATE_INDEX] > maxDate
    ) {
      dateTimes[END_DATE_INDEX] = this.originalRange[END_DATE_INDEX];
    }

    onSelect && onSelect(dateTimes);
  };

  setPickerRef = pickerRef => (this.pickerRef = pickerRef);

  /**
   * Update value for times
   */
  updateValue = (value: Array<Date>) => {
    // blueprint lib does not update time state when next value is the same day with the current day regard of the time,
    // so need to force it to setState
    this.pickerRef.setState({
      value,
      time: value
    });
  };

  /**
   * Convert value to prop for picker
   * @param {Object} props
   */
  convertValueForPicker(props: Object) {
    const { value, minDate, maxDate, hasRange } = props;
    if (isNil(value)) return null;

    if (hasRange && !isEmpty(value)) {
      const startDate = isNil(value[START_DATE_INDEX])
        ? null
        : isDateTimeValid(value[START_DATE_INDEX]) && value[START_DATE_INDEX] >= minDate
        ? value[START_DATE_INDEX].toDate()
        : minDate;
      const endDate = isNil(value[END_DATE_INDEX])
        ? null
        : isDateTimeValid(value[END_DATE_INDEX]) && value[END_DATE_INDEX] <= maxDate
        ? value[END_DATE_INDEX].toDate()
        : maxDate;
      return [startDate, endDate];
    }
    return isDateTimeValid(value) && value >= minDate && value <= maxDate
      ? value.toDate()
      : null;
  }

  componentWillReceiveProps(nextProps: Object) {
    const { hasRange, hasTime } = nextProps;
    if (hasRange && hasTime) {
      const newValue = this.convertValueForPicker(nextProps);
      this.updateValue(newValue);
    }
  }

  render() {
    const { hasRange, hasDate, hasTime, minDate, maxDate, locale, disabled, boundaryToModify } = this.props;
    const type = this.getType(hasRange, hasDate, hasTime);
    const { Component, defaultProps } = this.getComponent(type);
    const precision = hasTime ? 'second' : null;
    const selectedDate = this.convertValueForPicker(this.props);
    const restProps = assign(defaultProps, {
      value: selectedDate,
      formatDate: date => Moment.formatDate(date),
      parseDate: str => Moment.parseDate(str),
      onChange: hasRange ? this.onChangeRange : this.onChange,
      precision,
      timePrecision: precision,
      maxDate,
      minDate,
      locale,
      disabled,
      boundaryToModify
    });

    if (precision) {
      restProps.timePickerProps = {
        onChange: this.onChange
      };
    }

    const classNames = classnames({
      [Classes.ELEVATION_1]: hasDate,
      'three-sections': precision === 'second'
    });

    return (
      <div className='izenda-DateTimeCalendar'>
        <Component ref={this.setPickerRef} className={classNames} {...restProps} />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/DateTime/Izenda.DateTimeCalendar.js